import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Smartphone, Loader2, Search, Star, Plus } from "lucide-react";
import { BACKEND_URL } from "../../lib/api";

const API = BACKEND_URL;

const PLATFORM_TABS = [
  { key: "all", label: "All" },
  { key: "ios", label: "iOS" },
  { key: "android", label: "Android" },
];

const platformBadge = {
  ios: "bg-blue-50 text-blue-600 border border-blue-200",
  android: "bg-green-50 text-green-600 border border-green-200",
};

export default function AppSearchInput({ value, onChange, onSelectApp }) {
  const [query, setQuery] = useState(value || "");
  const [platform, setPlatform] = useState("all");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDrop, setShowDrop] = useState(false);
  const [selected, setSelected] = useState(null);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);
  const requestRef = useRef(0);
  const abortRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowDrop(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const search = (q, plat = platform) => {
    const requestId = ++requestRef.current;
    setQuery(q);
    onChange(q);
    setSelected(null);
    onSelectApp?.(null);
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = null;

    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q.trim() || q.trim().length < 2) {
      setResults([]);
      setShowDrop(false);
      setLoading(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      const controller = new AbortController();
      abortRef.current = controller;
      setLoading(true);
      try {
        const res = await fetch(
          `${API}/api/app-search?q=${encodeURIComponent(q.trim())}&platform=${plat}`,
          { signal: controller.signal }
        );
        if (!res.ok) throw new Error(`App search failed (${res.status})`);
        const data = await res.json();
        if (requestId !== requestRef.current) return;
        setResults(data.results || []);
        setShowDrop(true);
      } catch (error) {
        if (error.name === "AbortError" || requestId !== requestRef.current) return;
        setResults([]);
        setShowDrop(true);
      } finally {
        if (requestId === requestRef.current) setLoading(false);
      }
    }, 400);
  };

  const switchPlatform = (plat) => {
    setPlatform(plat);
    if (query.trim().length >= 2) search(query, plat);
    else {
      requestRef.current += 1;
      if (abortRef.current) abortRef.current.abort();
      setResults([]);
      setShowDrop(false);
      setLoading(false);
    }
  };

  useEffect(() => () => {
    requestRef.current += 1;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (abortRef.current) abortRef.current.abort();
  }, []);

  const select = (app) => {
    setSelected(app);
    setQuery(app.name);
    onChange(app.name);
    onSelectApp?.(app);
    setResults([]);
    setShowDrop(false);
  };

  const addTypedApp = () => {
    const app = { name: query.trim(), platform: null, url: null, app_not_listed: true };
    if (!app.name) return;
    setSelected(app);
    onChange(app.name);
    onSelectApp?.(app);
    setResults([]);
    setShowDrop(false);
  };

  return (
    <div ref={containerRef} className="relative">
      {/* Platform tabs */}
      <div className="flex gap-1 mb-2">
        {PLATFORM_TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => switchPlatform(tab.key)}
            className={`text-[10px] font-black uppercase tracking-widest px-2.5 py-1 border transition-colors ${
              platform === tab.key
                ? "bg-charcoal text-white border-charcoal"
                : "bg-white text-zinc-500 border-zinc-300 hover:border-charcoal"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Input */}
      <div className="relative">
        {selected?.icon ? (
          <img
            src={selected.icon}
            alt={selected.name}
            className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 rounded object-cover pointer-events-none"
          />
        ) : (
          <Smartphone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-electric pointer-events-none" />
        )}
        <input
          type="text"
          value={query}
          onChange={(e) => search(e.target.value)}
          onFocus={() => query.trim().length >= 2 && setShowDrop(true)}
          placeholder="Search your app name…"
          data-testid="audit-input-appname"
          className="w-full border-2 border-electric px-4 py-3 text-sm focus:outline-none placeholder-zinc-400 pl-10 pr-10"
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin text-zinc-400" />
          ) : (
            <Search className="w-4 h-4 text-zinc-300" />
          )}
        </span>
      </div>

      {/* Dropdown */}
      <AnimatePresence>
        {showDrop && query.trim().length >= 2 && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.15 }}
            className="absolute z-50 w-full bg-white border border-charcoal shadow-[4px_4px_0_0_#2196F3] mt-1 max-h-72 overflow-y-auto"
          >
            <button
              type="button"
              onClick={addTypedApp}
              className="sticky top-0 z-10 w-full flex items-center gap-3 px-4 py-3 text-left border-b border-red-200 bg-red-50 hover:bg-red-100 transition-colors"
            >
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-red-600 text-white">
                <Plus className="h-5 w-5" strokeWidth={2.5} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-bold text-red-700">Can’t find your app? Add your app</span>
                <span className="mt-0.5 block truncate text-xs text-red-600">Use “{query.trim()}” for your plan</span>
              </span>
            </button>
            {results.map((app) => (
              <button
                key={`${app.platform}-${app.id}`}
                type="button"
                onClick={() => select(app)}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-zinc-50 text-left border-b border-zinc-100 last:border-0 transition-colors"
              >
                {app.icon ? (
                  <img
                    src={app.icon}
                    alt={app.name}
                    className="w-10 h-10 rounded-xl flex-shrink-0 object-cover border border-zinc-100"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-xl flex-shrink-0 bg-zinc-100 grid place-items-center">
                    <Smartphone className="w-5 h-5 text-zinc-400" />
                  </div>
                )}

                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-charcoal truncate leading-tight">
                    {app.name}
                  </p>
                  <p className="text-xs text-zinc-500 truncate mt-0.5">
                    {app.developer}
                    {app.category ? ` · ${app.category}` : ""}
                  </p>
                  {app.rating > 0 && (
                    <span className="inline-flex items-center gap-0.5 text-[10px] text-zinc-400 mt-0.5">
                      <Star className="w-2.5 h-2.5 fill-amber-400 text-amber-400" />
                      {app.rating}
                    </span>
                  )}
                </div>

                <span
                  className={`text-[9px] font-black uppercase tracking-widest px-2 py-1 flex-shrink-0 ${platformBadge[app.platform]}`}
                >
                  {app.platform === "ios" ? "iOS" : "Android"}
                </span>
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      <p className="mt-1.5 text-[11px] text-zinc-500">
        Live search across App Store & Google Play.
      </p>
    </div>
  );
}
